import React from 'react';

import { getErrorMessage } from '~libs/axiosWrapper';

import { ErrorResponse, Spinner, SummaryStatCard } from '~components';
import Icon, { faEnvelope } from '~components/icon';

import { BulkSignupRecipient, MandateFilter, resolveMandateStates } from '~direct-debits/models';
import { useGetCountQuery } from '~direct-debits/actions/signups';

import { substituteMergeTags } from './mergeTags';

interface ComponentProps {
  value: {
    mandate_filter?: MandateFilter;
    has_outstanding_invoice?: boolean;
    has_email?: boolean;
    last_sent_days?: number;
    subject?: string;
    body?: string;
  };
}

const Review: React.FC<ComponentProps> = ({ value }) => {
  const { mandate_filter, has_email, has_outstanding_invoice, last_sent_days, subject, body } = value ?? {};

  const isEmpty = mandate_filter === undefined;
  const { data, error, isFetching, isError } = useGetCountQuery(
    {
      mandate_states: isEmpty ? [] : resolveMandateStates(mandate_filter),
      has_email,
      has_outstanding_invoice,
      last_sent_days,
      detailed: true,
    },
    { skip: isEmpty },
  );

  const count = data?.count ?? 0;
  const recipients: BulkSignupRecipient[] = data?.recipients ?? [];

  return (
    <div>
      {isFetching && <Spinner loading />}

      {!isFetching && (
        <SummaryStatCard
          count={<span>{count}</span>}
          title="Recipients"
          icon={<Icon icon={faEnvelope} />}
          countClassName="text-primary"
        />
      )}

      {isError && <ErrorResponse message={getErrorMessage(error)} />}

      {!isFetching && recipients.length > 0 && (
        <div className="mt-3">
          <h6 className="fw-semibold text-muted mb-2">Recipients</h6>
          <div className="bulk-signup-scroll-list border rounded">
            <table className="table table-sm table-striped mb-0">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                </tr>
              </thead>
              <tbody>
                {recipients.map((recipient) => {
                  const name = [recipient.first_name, recipient.surname].filter(Boolean).join(' ') || '—';
                  return (
                    <tr key={recipient.id}>
                      <td>{name}</td>
                      <td>{recipient.email}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!isFetching && (
        <div className="mt-3">
          <h6 className="fw-semibold text-muted mb-2">Preview (with example values)</h6>
          <div className="border rounded p-3 bg-white">
            <div className="mb-2">
              <span className="fw-semibold text-muted small">Subject:</span>
              <div>
                {subject ? (
                  substituteMergeTags(subject)
                ) : (
                  <span className="fst-italic text-muted">No subject</span>
                )}
              </div>
            </div>
            <div>
              <span className="fw-semibold text-muted small">Body:</span>
              <pre className="bulk-signup-email-body mb-0">
                {body ? (
                  substituteMergeTags(body)
                ) : (
                  <span className="fst-italic text-muted">No body</span>
                )}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Review;
